import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import {
  Instructor,
  Location,
  LocationArea,
  LocationAreaField,
  Offering,
  OfferingAppointment,
  OfferingCategory,
  OfferingFilters,
  Sport,
} from './models';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { OfferingFilterService } from './offering-filter.service';
import { ICustomerAppsConfig } from '../config/config.module';
import { HttpErrorHandler } from '../http/httpErrorHandler.service';
import { ConfigService } from '../config/config.service';
import { Injectable } from '@angular/core';
import { ApiDateConverter } from '../http/api-date-converter.service';
import { DataResult, mapDataResult$ } from '../http/httpDataResponse';
import { ActivatedRoute } from '@angular/router';
import { Department, DepartmentIdAndName } from '../department/department';
import { Team, TeamIdAndName } from '../team/model/team';

@Injectable({
  providedIn: 'root',
})
export class OfferingService<T extends OfferingAppointment> {
  constructor(
    protected httpClient: HttpClient,
    protected httpErrorHandler: HttpErrorHandler,
    protected configService: ConfigService<ICustomerAppsConfig>,
    protected offeringFilterService: OfferingFilterService,
    protected apiDateConverter: ApiDateConverter,
    protected route: ActivatedRoute,
  ) {}

  getOfferingAppointment(appointmentSeriesId: string, start: string): Observable<T> {
    return this.httpClient
      .get<DataResult<T>>(`${this.configService.config.apiUrl}/offerings/appointments/${appointmentSeriesId}/start/${start}`)
      .pipe(catchError(this.httpErrorHandler.handleErrorCallback), mapDataResult$<T>());
  }

  getOfferingAppointments(filters: OfferingFilters, page = 1, per_page: number, filterMemberId?: number): Observable<DataResult<T[]>> {
    const params = {
      ...this.offeringFilterService.offeringFiltersToApiQueryParams(filters),
      page,
      per_page,
    };

    if (filterMemberId) {
      params['filter_member_id'] = filterMemberId;
    }

    return this.httpClient
      .get<DataResult<T[]>>(`${this.configService.config.apiUrl}/offerings/appointments`, {
        params,
      })
      .pipe(catchError(this.httpErrorHandler.handleErrorCallback));
  }

  getOfferingAppointmentFromRoute(): Observable<T | null> {
    return this.route.queryParams.pipe(
      distinctUntilChanged((a, b) => a.appointmentSeriesId === b.appointmentSeriesId && a.start === b.start),
      switchMap((queryParams) => {
        if (queryParams.appointmentSeriesId && queryParams.start) {
          return this.getOfferingAppointment(queryParams.appointmentSeriesId, queryParams.start).pipe(catchError(() => of(null)));
        } else {
          return of(null);
        }
      }),
    );
  }

  geOfferingCategories(filterMemberId?: number): Observable<OfferingCategory[]> {
    const params = {};
    if (filterMemberId) {
      params['filter_member_id'] = filterMemberId;
    }

    return this.httpClient
      .get<DataResult<OfferingCategory[]>>(`${this.configService.config.apiUrl}/offerings/categories`, { params })
      .pipe(catchError(this.httpErrorHandler.handleErrorCallback), mapDataResult$<OfferingCategory[]>());
  }

  getSports(filterMemberId?: number): Observable<Sport[]> {
    const params = {};
    if (filterMemberId) {
      params['filter_member_id'] = filterMemberId;
    }

    return this.httpClient
      .get<DataResult<Sport[]>>(`${this.configService.config.apiUrl}/offerings/sports`, { params })
      .pipe(catchError(this.httpErrorHandler.handleErrorCallback), mapDataResult$<any[]>());
  }

  getDepartments(filterMemberId?: number): Observable<DepartmentIdAndName[]> {
    const params = {};
    if (filterMemberId) {
      params['filter_member_id'] = filterMemberId;
    }

    return this.httpClient
      .get<DataResult<DepartmentIdAndName[]>>(`${this.configService.config.apiUrl}/offerings/departments`, { params })
      .pipe(catchError(this.httpErrorHandler.handleErrorCallback), mapDataResult$<any[]>());
  }

  getTeams(filterMemberId?: number): Observable<TeamIdAndName[]> {
    const params = {};
    if (filterMemberId) {
      params['filter_member_id'] = filterMemberId;
    }

    return this.httpClient
      .get<DataResult<TeamIdAndName[]>>(`${this.configService.config.apiUrl}/offerings/teams`, { params })
      .pipe(catchError(this.httpErrorHandler.handleErrorCallback), mapDataResult$<any[]>());
  }

  getInstructors(filterMemberId?: number): Observable<Instructor[]> {
    const params = {};
    if (filterMemberId) {
      params['filter_member_id'] = filterMemberId;
    }

    return this.httpClient
      .get<DataResult<Instructor[]>>(`${this.configService.config.apiUrl}/offerings/instructors`, { params })
      .pipe(catchError(this.httpErrorHandler.handleErrorCallback), mapDataResult$<any[]>());
  }

  getLocations(filterMemberId?: number): Observable<Location[]> {
    const params = {};
    if (filterMemberId) {
      params['filter_member_id'] = filterMemberId;
    }

    return this.httpClient
      .get<DataResult<Location[]>>(`${this.configService.config.apiUrl}/offerings/locations`, { params })
      .pipe(catchError(this.httpErrorHandler.handleErrorCallback), mapDataResult$<any[]>());
  }

  getSeriesAppointmentsForOfferingId<T extends OfferingAppointment>(offeringId: string): Observable<T[]> {
    return this.httpClient
      .get<DataResult<T[]>>(`${this.configService.config.apiUrl}/offerings/offering/${offeringId}/appointments`)
      .pipe(catchError(this.httpErrorHandler.handleErrorCallback), mapDataResult$<T[]>());
  }

  toggleFavorite(offering: Offering): Observable<Offering> {
    const url = `${this.configService.config.apiUrl}/offerings/${offering.id}/favorite`;
    let stream: Observable<void>;
    if (offering.isFavorite) {
      stream = this.httpClient.delete<void>(url);
    } else {
      stream = this.httpClient.post<void>(url, {});
    }

    return stream.pipe(
      catchError(this.httpErrorHandler.handleErrorCallback),
      map(() => {
        return {
          ...offering,
          isFavorite: !offering.isFavorite,
        };
      }),
    );
  }

  getAreasFromLocations(locations: Location[]): LocationArea[] {
    return locations.reduce((areas: LocationArea[], location) => {
      return areas.concat(location.areas);
    }, []);
  }

  getFieldsFromLocations(locations: Location[]): LocationAreaField[] {
    return this.getAreasFromLocations(locations).reduce((fields: LocationAreaField[], locationArea) => {
      return fields.concat(locationArea.fields);
    }, []);
  }
}
